import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import './idblab-bio-card-cta/idblab-bio-card-cta';
import componentStyles from './idblab-bio-card.css.lit';
import { MobileController } from 'src/components/controllers/mobile-controller';
import { OutlineContentCard } from 'src/components/base/outline-content-card/outline-content-card';
import { IdbLabBioCardCta } from './idblab-bio-card-cta/idblab-bio-card-cta';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';

export type AllowedLabColors =
  | 'light-gray'
  | 'gray-dark'
  | 'gray'
  | 'light-blue'
  | 'blue'
  | 'green'
  | 'white'
  | 'transparent'
  | 'black';
/**
 * The IDB Biography Card component
 * @element idblab-bio-card
 * @slot title - The name of the person. `h4`
 * @slot content - A wrapper for `p` and `idb-link` elements containing the content of the card
 * @slot image - the image slot for the persons portrait
 */
@customElement('idblab-bio-card')
export class IdbLabBioCard extends OutlineContentCard {
  mobileController = new MobileController(this, 'xs');
  static styles: CSSResultGroup = [componentStyles];

  @query('idblab-bio-card-cta')
  cta: IdbLabBioCardCta;

  /**
   * Inverts the bg-color and hover:bg-color
   */
  @property({ type: Boolean, attribute: 'inverted' })
  inverted = true;

  /**
   * Unique ID that matdches the ID of the respective modal
   */
  @property({ type: String, attribute: 'modal-id' })
  modalId: string;

  /**
   * Aria label to pass to bio card cta
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  /**
   * Text to display in the cta
   */
  @property({ type: String, attribute: 'cta-text' })
  ctaText = 'VIEW BIOGRAPHY';

  /**
   * The url for the article or media type that creates the link for the card
   */
  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  /**
   * Component specific prop for background color
   */
  @property({ type: String, attribute: 'card-background' })
  labCardBackground: AllowedLabColors;

  render(): TemplateResult {
    const cardClasses = {
      card: true,
      [`outline-bg-${this.labCardBackground}`]:
        this.labCardBackground !== undefined,
    };
    const imageClasses = {
      'card-image': true,
      'image-type--profile': true,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
    };
    const titleClasses = {
      'card-title': true,
    };
    const contentClasses = {
      'card-content': true,
    };
    const ctaClasses = {
      'cta-wrapper': true,
      'card-cta': true,
    };

    return html`
      <section class="${classMap(cardClasses)}">
        ${this.imageTemplate(imageClasses)}
        <section class="${classMap(contentWrapperClasses)}">
          ${this.titleTemplate(titleClasses)}
          ${this.contentTemplate(contentClasses)}
          ${this.modalId ? this.ctaTemplate(ctaClasses) : ''}
        </section>
      </section>
    `;
  }

  ctaTemplate(classes: ClassInfo): TemplateResult | null {
    return html`

      <section class="${classMap(classes)}">
        <idblab-bio-card-cta
          slot="cta"
          icon-right=${this.mobileController.isMobile ? '' : 'chevron-right'}
          button-variant="${this.labCardBackground == 'gray' ? 'none' : 'red' }"
          button-label=${this.buttonLabel}
          @click=${this.handleCtaClick}
        >
          ${this.ctaText.toUpperCase()}
        </idblab-bio-card-cta>
      </section>`;
  }

  handleCtaClick = (): void => {
    const event = new CustomEvent('modal-open', {
      bubbles: true,
      detail: {
        id: `${this.modalId}`,
      },
    });
    document.dispatchEvent(event);
  };

  imageTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasImageSlot) {
      return html`
        <div class="image-profile--placeholder-image">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="208" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-user"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
        </div>`;
    } else {
      return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('image')}
    </section>`;
    }
  }
}
